'use client';

/**
 * Le bouton micro d'OGRYN — celui du chat d'Otto, et le seul qu'on pose ailleurs.
 *
 * Son comportement EST sa définition, c'est pour ça qu'il vit dans son propre
 * fichier plutôt que recopié à chaque endroit qui dicte :
 *
 *  · le bouton ne change jamais — même bosse, même taille, même place. Un bouton
 *    qui se transforme sous le doigt déplace la cible ;
 *  · c'est l'ICÔNE qui vire au rouge et qui bat au rythme de la voix ;
 *  · l'onde n'est pas un cercle : c'est la même icône, dupliquée deux fois
 *    derrière la vraie, chacune battant plus fort que la précédente. Au silence
 *    les trois se superposent exactement — on n'en voit qu'une.
 *
 * Tout est en `transform` + `opacity` : l'échelle est composée sur le GPU, elle
 * peut donc être réécrite à chaque mesure du micro sans une seule mise en page.
 */

/**
 * Les deux calques d'onde, du fond vers l'avant. `ax`/`ay` sont des AMPLITUDES,
 * pas des échelles finales : l'échelle vaut `1 + a·amp`. La distinction a son
 * importance — les avoir lues comme des échelles ramenait l'amplitude verticale
 * du calque avant à 0,05 au lieu de 1,05, soit vingt fois trop peu : le micro
 * ne bougeait plus.
 *
 * `ax > ay` : l'onde ne grossit pas homothétiquement, elle S'ÉLARGIT en
 * s'éloignant — c'est ce que fait un son qui se propage, et c'est ce qui la
 * distingue d'une icône simplement zoomée.
 *
 * `o0` opacité au silence, `of` ce qu'elle perd à pleine voix : une onde
 * s'efface en s'écartant, sinon elle se lit comme un calque posé là.
 */
const LAYERS = [
  { ax: 3.20, ay: 2.40, o0: 0.30, of: 0.10 },
  { ax: 1.75, ay: 1.35, o0: 0.45, of: 0.10 },
] as const;

/**
 * Réponse du geste à la voix. La mesure brute est un RMS : une conversation
 * normale y vit entre 0,05 et 0,3, donc les trois quarts de la course visuelle
 * ne servaient jamais. La racine (exposant 0,55) redresse le bas de l'échelle —
 * 0,1 devient 0,29, 0,25 devient 0,47 — sans plafonner les pics. C'est de la
 * mise en scène, pas de la mesure : elle reste locale au bouton, `meterStream`
 * continue de publier la vraie amplitude à qui en a besoin.
 */
const amplify = (v: number) => Math.pow(Math.min(1, Math.max(0, v)), 0.55);

export function MicButton({ recording, level, onToggle, disabled, busy, size = 48, label, solid = false, className = '' }: {
  recording: boolean;
  /** Amplitude de la voix, 0 → 1. Ignorée hors enregistrement. */
  level: number;
  onToggle: () => void;
  disabled?: boolean;
  /** Traitement en cours (transcription, extraction) : le micro cède la place à
   *  un rouet et n'accepte plus le doigt. */
  busy?: boolean;
  /** Diamètre en px. L'icône suit à 40 % — la proportion du bouton du chat. */
  size?: number;
  /** Remplace l'intitulé d'accessibilité au repos (« Dicter » par défaut). */
  label?: string;
  /** Bouton PLEIN, posé sur le contenu (pastille flottante) au lieu d'une bosse
   *  dans la matière : la bosse ne se voit pas sur un aplat, et un micro qui
   *  flotte au-dessus d'une liste doit se détacher comme le « + » des pôles
   *  Promo et Pièces. Le fond vient de `className`. */
  solid?: boolean;
  className?: string;
}) {
  const amp = recording ? amplify(level) : 0;
  const icon = Math.round(size * 0.4);
  return (
    <button
      type="button"
      /* `onPointerDown` en enregistrement : on coupe au contact, pas au relâchement
         — attendre le `click` donne une demi-seconde de silence enregistré. Et
         surtout : un `click` sur une cible dont le transform bouge à chaque image
         est annulé par le navigateur (pointerdown et pointerup ne visent plus la
         même géométrie) — l'enregistrement devenait impossible à arrêter. */
      {...(recording ? { onPointerDown: onToggle } : { onClick: onToggle })}
      disabled={disabled || busy}
      aria-label={recording ? 'Terminer l’enregistrement' : busy ? 'Traitement en cours' : label ?? 'Dicter'}
      aria-pressed={recording}
      style={{ width: size, height: size }}
      className={`relative rounded-full flex items-center justify-center shrink-0 disabled:opacity-40 ${
        recording ? 'text-red-500' : solid ? 'active:scale-95 transition-transform' : 'text-[var(--otto-ink)] active:scale-95 transition'
      } ${className}`}
    >
      {!solid && <span aria-hidden style={{ filter: 'blur(2.5px)' }} className="absolute inset-0 rounded-full shadow-[var(--sh-emboss)]" />}

      {busy ? (
        <span aria-hidden className="relative rounded-full border-2 border-current border-t-transparent animate-spin"
          style={{ width: icon, height: icon }} />
      ) : (<>
        {recording && LAYERS.map((l, i) => (
          /* `pointer-events-none` : la cible du doigt reste le bouton, quoi que
             fassent les ondes. */
          <span key={i} aria-hidden className="absolute pointer-events-none will-change-transform"
            style={{
              transform: `scale(${(1 + amp * l.ax).toFixed(3)}, ${(1 + amp * l.ay).toFixed(3)})`,
              opacity: Math.max(0, l.o0 - amp * l.of).toFixed(3),
            }}>
            <MicIcon size={icon} />
          </span>
        ))}

        <span className="relative pointer-events-none will-change-transform"
          style={recording ? {
            transform: `scale(${(1 + amp * 0.5).toFixed(3)})`,
            // Halo qui respire avec la voix : la lueur naît de l'icône elle-même,
            // elle ne se pose pas dessus.
            filter: `drop-shadow(0 0 ${(3 + amp * 12).toFixed(1)}px rgba(239,68,68,${(0.4 + amp * 0.5).toFixed(2)}))`,
          } : undefined}>
          <MicIcon size={icon} />
        </span>
      </>)}
    </button>
  );
}

export function MicIcon({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  );
}
